import CheckCircle from "assets/images/settings/checkCircle.svg";
import CheckCircleWhite from "assets/images/settings/checkCircleWhite.svg";
import _ from "lodash";
import React, { useCallback, useState } from "react";
import styled from "styled-components";
import { colors, tokens } from "../../../utils";
import { device } from "../../../utils/device";
import Button from "../../Button";
import TextView from "../../TextView";
import EssentialPackageIcon from "@assets/images/packages/essentialPackageIcon.svg";
import FreePackageIcon from "@assets/images/packages/FreePackage.svg";
import PremiumPackageIcon from "@assets/images/packages/premiumPackageIcon.svg";
import ProSuiteIcon from "@assets/images/ProSuite.svg";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
// import CoinIcon from "@assets/images/segmentEditor/coin.svg";
// import CoinsIcon from "@assets/images/segmentEditor/coins.svg";
import { StyledTooltip } from "../../IyugaTooltip";
import { REFERRAL_PROGRAM_SUSPENDED } from "../../../constants/featureFlags";
import { stripSuspendedReferralCopy } from "./stripSuspendedReferralCopy";
import { formatAnnualTotal } from "../../../utils/annualPricing";
import { resolveAnnualPlanCard } from "../../../utils/annualCardDisplay";
import { resolveAppStoreManagement } from "../../Settings/Subscription/storeManagementLinks";
// import CloseIcon from "@mui/icons-material/Close";
// import { Box, IconButton, Modal as MUIModal, Typography } from "@mui/material";
// import Coins3dIcon from "@assets/images/Coins3d.svg";

import { resolveCardFeatures } from "../../../constants/subscriptionCardFeatures";
import { PAID_LOCKS_ENABLED, SETTINGS_CRUNCH_FIX } from "../../../constants/featureFlags";
import { planNameNoWrapStyle } from "../../../constants/settingsCrunchFix";
import {
  displayFeatureDescription,
  resolvePaidLocksLive,
  resolveSubscriptionCtaLabel,
} from "./subscriptionItemLogic";
import { isUnpricedPaidTier } from "../../../constants/subscriptionFeatures";
import { featureInfoIconStyle } from "./featureInfoIconStyle";

// WL-042 / ISP-9999: when the paid-locks feature is live, the Premium/ProSuite
// "Paid Locks (Coming Soon)" tier-card bullet becomes a shipped feature — drop
// the "(Coming Soon)" qualifier at render time (applied here, not by editing
// the data, so it also catches the server-driven `feature_splits` copy).
//
// ISP-9999 (fix-editwheel-subscription-copy-paid-locks-coming-soon,
// 2026-09-06): this used to ALSO require a `GET /api/v2/wheel/0/access/`
// probe (the "PAYWALL_PROBE_WHEEL_ID" sentinel) to confirm
// `feature_enabled:true` before dropping the marker. That probe can never
// succeed — wheel id 0 never exists, so the backend 400s "Wheel not found"
// instead of ever returning `feature_enabled` (see
// hooks/usePaymentLocking.js's `useWheelPaywallAccess`, and
// yuga_wheel/api/v2/viewsets/wheel/actions/paywall.py::paywall_access on the
// backend, which resolves the wheel BEFORE returning `feature_enabled`).
// `resolvePaidLocksLive` was therefore permanently `false`, and this card kept
// showing "Paid Locks (Coming Soon)" live on web-dev (main.99ef90b9.js,
// measured 2026-09-06) even though Paid Locks is demonstrably live in
// production (a completed Stripe purchase moved a real buyer from
// view_only_teaser to unlocked_view) and PR #5039 already confirmed backend
// liveness via a REAL wheel id's access probe. See subscriptionItemLogic.js
// for the full history. `PAID_LOCKS_ENABLED` (REACT_APP_PAID_LOCKS) is now
// trusted directly: it is the only signal available with no wheel-agnostic
// backend endpoint to ask, and it is ops-controlled per environment to mirror
// the paired backend flag.

// Styled Components
const Container = styled.div`
  border-radius: 8px;
  background-color: ${colors.background};
  display: flex;
  flex-direction: column;
  gap: 20px;
  box-shadow: 0px 0px 5.9px 0px #00000040;
  border: ${(props) =>
    props.isInfinity ? `2px solid ${colors.yellowDark}` : "none"};
`;

// T22.758: border-box so the equal-height min-height TiersScrollRow writes
// onto every card's header (see equalizeCardHeaders) is the real height.
const Header = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 20px;
  padding-bottom: 0px;
  flex-shrink: 0;
`;

// relost-freeze-reverify-mktg-768-price-wordwrap: was a fixed `height: 40px`
// + `overflow: hidden` + `white-space: nowrap` with no `flex-wrap`. At 768px
// the tier name + icon + "Most Popular" badge don't all fit on one row, and
// the overflowing badge was hard-clipped at the row's right edge instead of
// wrapping (live-measured: Premium's badge clipped to "Mo" beside the tier
// icon). `min-height` keeps the same 40px reservation for the common
// one-row case, and `flex-wrap: wrap` lets a second row form instead of
// clipping.
export const TitleRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  min-height: 40px;
`;

const DiscountBadge = styled.div`
  background-color: #e87033;
  padding: 5px 10px;
  border-radius: 4px;
  width: max-content;
`;

// T22.756: the list had no bottom padding, so on the card with the most rows
// (Premium: "Customizable Ad Space (1)") the last row -- and its 32px info-icon
// hit box, which overhangs the 20px row by 6px -- sat flush on the "View all
// Features" border-top below it (live-measured gap 0px at 768/1024/1440,
// web-stg main.cfad8a37). 12px clears the icon overhang with room to spare.
export const FEATURES_LIST_BOTTOM_PAD_PX = 12;
export const FeaturesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  padding: 0px 12px ${FEATURES_LIST_BOTTOM_PAD_PX}px 9px;
`;

export const FeatureItem = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 3px;

  svg {
    font-size: 16px;
    color: #29c18c;
    flex-shrink: 0;
  }
`;

const FeaturesLink = styled(TextView)`
  color: ${(props) => (props.isActive ? colors.white : colors.PrimaryPurple)};
  display: flex;
  border-top: ${(props) =>
    props.isActive
      ? `1px solid ${colors.white}`
      : `1px solid ${colors.lightGrey}`};
  padding: 0px 10px;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  text-align: center;
  height: 60px;
  @media (max-width: 600px) {
    display: none;
  }
`;

const AnnualPrice = styled.div`
  display: flex;
  align-self: center;
  align-items: end;
  justify-content: center;
  width: 100%;
  background-color: ${colors.background};
  gap: 5px;
`;

export const ButtonContainer = styled.div`
  display: flex;
  flex-shrink: 0;
  align-items: flex-start;

  /* T22.761: plan CTAs ("Subscribe to Premium", "Current Plan") inherited
     Button's 32px desktop min-height; give them the 44px target everywhere. */
  > button {
    min-height: 44px;
  }
`;

// T22.756: was a fixed `height: 65px/75px` + `overflow: hidden` with a
// 4/5-line clamp -- but 4-5 lines of this 20px-line-height copy need 80-100px,
// so as soon as a card narrowed the blurb was cut mid-line (live-measured on
// web-stg main.cfad8a37: Essential's 100px blurb in a 75px box at 768px,
// 3 of 4 cards cut at 1024px). `min-height` keeps the same reservation for the
// common short-blurb case (unchanged at 1440px, where every blurb fits) and
// lets a longer blurb grow the box instead of being clipped.
export const Description = styled.div`
  min-height: ${(props) => (props.isMonthly ? "65px" : "75px")};
  margin-bottom:3px;
`;

// QA-129 (ISP-9999): was a fixed `height: 74px`. When a card narrows enough
// that the price row's 3 lines (struck-through original price, current
// price, "$X Billed annually" pill) wrap, the box's fixed height doesn't
// grow with them -- the overflowing content isn't clipped (no
// overflow:hidden), it just spills past the box's bottom edge, into/behind
// the ButtonContainer that follows it in the flex column below. `min-height`
// keeps the same 74px reservation for the common 2-line case (unchanged
// layout there) but lets the box grow when the content genuinely needs more
// room, so nothing downstream ever overlaps it.
export const PriceContainer = styled.div`
  min-height: 74px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  justify-content: flex-start;
`;

// PLQA-C-006: this row always occupies the same vertical space (matching the
// struck-through price's own "subtitle" TextView line-height) whether or not
// a tier is discounted. Before this, the struck-through line only rendered
// -- and only took up space -- on discounted cards (Premium/ProSuite),
// so their PriceContainer grew ~1 line taller than Free/Essential's and
// pushed the Subscribe CTA row down, an 8px misalignment at 1440px.
// Reserving the row unconditionally keeps every card's CTA on one baseline.
export const StrikethroughRow = styled.div`
  height: 20px;

  @media ${device.xl} {
    height: 24px;
  }
`;

export const AnnualPriceContainer = styled.div`
  height: 100px;
`;

export const FeatureItemContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex-grow: 1; /* Allow it to grow and take available space */
`;

const EarningsProgramContainer = styled.div`
  width: 100%;
  height: 32px;
  border-radius: 8px;
  padding: 8px;
  background: #d4e2f280;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  align-self: center;
`;

const EarningsProgramWrapper = styled.div`
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 4px;
`;

// relost-freeze-reverify-mktg-768-price-wordwrap: once TitleRow can wrap
// (above), the badge itself must never wrap its own text onto two lines or
// get squeezed narrower by its flex siblings once it drops to its own row.
export const BestDealContainer = styled.div`
  background-color: #f5cc3b;
  padding: 2px 8px;
  border-radius: 16px;
  color: #123051;
  font-weight: 700;
  white-space: nowrap;
  flex-shrink: 0;
`;

const AnnualPriceValue = styled.div``;

const AnnualSavingsLine = styled.div`
  color: ${(props) => (props.$onDark ? "#d1fae5" : "#17803d")};
  font-size: 13px;
  font-weight: 600;
  text-align: center;
  line-height: 18px;
`;

const AnnualStrikeStack = styled.div`
  min-height: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const AnnualPromoBox = styled.div`
  margin-top: 4px;
  margin-bottom: 8px;
  border: 1.5px dashed ${(props) => (props.$onDark ? "rgba(255,255,255,0.9)" : "#7c3aed")};
  border-radius: 10px;
  padding: 8px 10px 10px;
  text-align: center;
  color: ${(props) => (props.$onDark ? "#ffffff" : "#3b0764")};
  line-height: 1.35;
`;

function AnnualYearlyPrice({ card, activeItem, showFeatures }) {
  const ink = activeItem ? colors.white : "#1f2937";
  const muted = activeItem ? "rgba(255,255,255,0.85)" : "#565556";
  return (
    <>
      <AnnualStrikeStack data-testid="annual-strikethrough-row">
        {card.compareAt && (
          <TextView
            variant="subtitle"
            style={{
              textDecoration: "line-through",
              color: activeItem ? colors.white : colors.lightGrey,
            }}
          >
            {card.compareAt}
          </TextView>
        )}
      </AnnualStrikeStack>
      <TextView style={{ textAlign: "center" }}>
        <span style={{ whiteSpace: "nowrap" }}>
          <span
            data-testid="annual-yearly-price"
            style={{
              fontWeight: 700,
              fontSize: "28px",
              lineHeight: 1,
              color: ink,
              textDecoration: card.strikeHeadline ? "line-through" : "none",
            }}
          >
            ${card.headline}
          </span>
          <span
            style={{
              color: muted,
              marginLeft: "2px"
            }}
          >
            /year
          </span>
        </span>
        {card.footnote && (
          <span style={{ color: muted, marginLeft: "6px", fontSize: "13px", whiteSpace: "nowrap" }}>
            {card.footnote}
          </span>
        )}
      </TextView>
      {card.savingsLabel && (
        <AnnualSavingsLine data-testid="annual-savings-line" $onDark={activeItem}>
          {card.savingsLabel}
        </AnnualSavingsLine>
      )}
      {card.promo && (
        <AnnualPromoBox
          data-testid="annual-promo-box"
          $onDark={activeItem}
          style={!showFeatures ? { marginBottom: 28 } : undefined}
        >
          <div style={{ fontSize: "11px", fontWeight: 700, letterSpacing: "0.04em" }}>
            ★ SPECIAL PROMOTION ★
          </div>
          <div style={{ marginTop: "4px" }}>
            <span style={{ textDecoration: "line-through", marginRight: "6px" }}>
              ${card.promo.struck}
            </span>
            <span style={{ fontWeight: 700, fontSize: "20px" }}>${card.promo.price}</span>
            {card.promo.firstYear ? " first year" : ""}
          </div>
          <div style={{ fontSize: "12px", marginTop: "2px" }}>{card.promo.detail}</div>
        </AnnualPromoBox>
      )}
    </>
  );
}

// Enterprise "Let's talk" CTA — a real, keyboard-reachable mailto anchor styled to
// match the sibling outlined tier buttons. Text uses PrimaryPurple700 (#8126DB,
// ~6.5:1 on white) rather than PrimaryPurple (#9B5AF8, ~4.03:1) so the label meets
// WCAG-AA contrast. 44px min touch target + a visible :focus-visible outline.
const ContactSalesLink = styled.a`
  width: 90%;
  margin: auto;
  min-height: 44px;
  padding: 6px 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  border: 1px solid ${colors.PrimaryPurple};
  background-color: ${colors.white};
  color: ${colors.PrimaryPurple700};
  font-family: "DM Sans", sans-serif;
  font-weight: 700;
  font-size: 14px;
  text-align: center;
  text-decoration: none;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${colors.PrimaryPurple100};
  }

  &:focus-visible {
    outline: 2px solid ${colors.PrimaryPurple700};
    outline-offset: 2px;
  }
`;

export default function SubscriptionItem({
  item,
  onViewFeatures,
  onActionPress,
  showFeatures = true,
  style,
  isAnnually,
  // SUB-A4: when true (the active sub was purchased on the mobile app),
  // this card's Upgrade/Cancel/Subscribe action button is replaced by a
  // manage-in-app-store button (READ everywhere, CHANGE where bought).
  appStoreManaged = false,
  // ISP-9999 (Option B4, fe-subB4-plan-move-flow): when true (flag ON,
  // caller-resolved), a non-active card under appStoreManaged gets a "Start
  // on Web" CTA instead of "Manage in App Store", opening MovePlanModal.
  // The card for the user's OWN current store plan still shows "Manage in
  // App Store" -- there is nothing to move it to.
  onMovePlanPress = null,
}) {
  const {
    name,
    per_month_cost,
    orig_per_month_cost,
    short_description,
    is_active,
    action,
    sub_tier,
    feature_splits,
    card_quotas,
    stripe_product_id,
    price,
    // Enterprise (sales-led) card fields — present only on the flag-gated
    // Enterprise data entry (see Settings/Subscription/enterpriseCard.js).
    is_enterprise,
    contact_email,
    cta_label,
    features: enterpriseFeatures,
  } = item;

  // Paid Locks live signal (subscriptionItemLogic). See the ISP-9999 comment
  // near the top of this file / in subscriptionItemLogic.js for why this is
  // now just the client build flag: the previous id-0 access-probe gate could
  // never resolve true and left this card permanently stuck on "(Coming
  // Soon)" regardless of real backend state.
  const paidLocksLive = resolvePaidLocksLive({
    clientFlagEnabled: PAID_LOCKS_ENABLED,
  });

  // nem-sub-tooltip-overlap-cta: the feature-row (i) tooltip below needs a
  // collision boundary so it never escapes past this card's own edges into a
  // neighboring tier card's CTA on the tight pricing grid. A plain useRef
  // wouldn't work here: the modifiers array embedding it is built during
  // THIS render, before the ref attaches, and nothing else re-renders this
  // component afterward (the hover state that opens the tooltip lives
  // inside MUI's own Tooltip, not here) — so a ref alone would forever
  // pass a stale `null` boundary. State + a callback ref forces the one
  // extra render needed to pick up the real, mounted node.
  //
  // Bounded to this feature list's own node (below the Subscribe
  // ButtonContainer), NOT the whole card: an earlier version bounded to the
  // whole card container, which also contains that Subscribe button, so
  // Popper never treated painting over it as "overflow" -- a `placement=
  // "top"` tooltip on the first 1-2 feature rows (closest to the button,
  // only the Container's 20px flex gap away) rendered directly on top of
  // the CTA on live measurement. Bounding here instead makes escaping
  // upward past the button register as real overflow, so flip picks a
  // placement that actually fits.
  const [featureListEl, setFeatureListEl] = useState(null);
  const featureListRef = useCallback((node) => setFeatureListEl(node), []);

  const activeItem = is_active || action === "ACTIVE";

  // Annual strikethrough uses `orig_per_month_cost` from `/subscription/packages/`
  // as-is. When it equals `per_month_cost` (discount_percent 0 on production
  // annual tiers), no struck-through line is shown. When the API sends distinct
  // values (e.g. ambassador coupon: per 0, orig 41.67), show the API original.
  const displayedOriginalCost = orig_per_month_cost;
  const isDiscounted = parseFloat(displayedOriginalCost) !== parseFloat(per_month_cost);

  // PLQA-C-006: `orig_per_month_cost`/`per_month_cost` drift in TYPE across
  // tiers (Free's API value is the number 0, Business's is the string
  // "0.00" from GET /subscription/packages/) -- a bare `== 0` on one field
  // and `!== 0` on the other can disagree on a string zero, since neither
  // coerces the same way. One `parseFloat(...) === 0` backs every zero-cost
  // check below so every self-serve free-priced tier (Free today, any
  // future string-typed zero tier) is treated identically.
  const isFreeTier = parseFloat(orig_per_month_cost) === 0;
  // T22.617: the Free card's title already reads "Free", so a huge "Free" in
  // the price slot said it twice (Gordon, phone). When the plan name is the
  // word itself, the price slot shows "$0/month" like its paid siblings; a
  // differently named zero-cost tier (e.g. Business) keeps the "Free" label.
  const priceRepeatsName =
    isFreeTier && String(name || "").trim().toLowerCase() === "free";

  // PLQA-C-002: a paid tier reaching the client with no Stripe product
  // linked yet (e.g. Business today) is "not configured for self-serve
  // purchase", not "$0 forever" -- rendering it through the same "Free"
  // label as sub_tier 0 directly contradicted the tier's own per-seat
  // billing copy. See isUnpricedPaidTier (subscriptionFeatures.js).
  //
  // Excludes the user's OWN active/held plan (PLQA-C-006 already covers a
  // held Business-shaped ($0 string) plan rendering "Free" with a disabled
  // "Current Plan" CTA): there is no upsell/checkout to fail closed on for a
  // tier the user is already holding, so this fallback only applies in the
  // purchase/upsell context where the CTA would otherwise offer a checkout
  // that 400s.
  const isUnpriced =
    !is_enterprise &&
    !activeItem &&
    isUnpricedPaidTier({
      subTier: sub_tier,
      perMonthCost: orig_per_month_cost,
      stripeProductId: stripe_product_id,
      priceUnavailable: price?.price_unavailable,
    });

  const annualCard = isAnnually && !isUnpriced ? resolveAnnualPlanCard(item) : null;

  // SUB-005: the server-driven `feature_splits` is the single source of truth
  // for self-serve card bullets (TIER_FEATURES only as fallback inside
  // resolveCardFeatures). The flag-gated Enterprise data entry carries its
  // bullets on the item itself (unit E4).
  const featureItems = is_enterprise
    ? enterpriseFeatures || []
    : resolveCardFeatures({
        featureSplits: feature_splits,
        subTier: sub_tier,
        cardQuotas: card_quotas,
      });

  // const [openEarningsModal, setOpenEarningsModal] = React.useState(false);

  // const handleOpenEarningsModal = () => {
  //   setOpenEarningsModal(true);
  // };

  // const handleCloseEarningsModal = () => {
  //   setOpenEarningsModal(false);
  // };

  // The label rule lives in subscriptionItemLogic (pure, unit-tested) so the
  // tier ladder can be re-proved without rendering three tiers in a browser.
  const getButtonText = () =>
    resolveSubscriptionCtaLabel({
      isActive: activeItem,
      isAnnually,
      subTier: sub_tier,
      action,
      name,
    });

  return (
    <Container
      style={{
        // DL-030 (ISP-4018): the current-plan card carries WHITE title/price/
        // feature text, so its fill must be the design-system ON-FILL purple
        // (tokens.brand.primaryActive #8F38EF, white 5.28:1 AA) — not the
        // semi-transparent accent purpleGradient #9B5AF8B5, on which the same
        // white text was only ~2.9:1 (FAILS AA). on-fill != accent (see #1919).
        backgroundColor: activeItem ? tokens.brand.primaryActive : "transparent",
        height: !showFeatures ? "auto" : "100%",
        minHeight: !showFeatures ? "340px" : undefined,
        paddingBottom: !showFeatures ? "24px" : undefined,
        boxSizing: "border-box",
        borderRadius: !showFeatures ? "0px" : "8px",
        ...style,
      }}
      {...(!showFeatures ? { "data-compare-card": "" } : {})}
    >
      <Header data-plan-card-header="">
        <TitleRow>
          <TextView
            variant="subtitle"
            style={{
              fontWeight: 700,
              whiteSpace: "nowrap",
              flexShrink: 0,
              ...(activeItem && { color: colors.white }),
              // settings-crunch: OFF -> undefined (spread adds nothing,
              // byte-identical); ON -> nowrap+ellipsis so a tier name like
              // "Premium" degrades to an ellipsis instead of a mid-word break.
              ...(planNameNoWrapStyle(SETTINGS_CRUNCH_FIX) || {}),
            }}
          >
            {name}
          </TextView>
          {sub_tier === 0 && (
            <img
              src={FreePackageIcon}
              alt="Free Tier Icon"
              style={{ width: "20px", height: "20px" }}
            />
          )}
          {sub_tier === 1 && (
            <img
              src={EssentialPackageIcon}
              alt="Essential Tier Icon"
              style={{ width: "20px", height: "20px" }}
            />
          )}
          {sub_tier === 2 && (
            <>
              <img
                src={PremiumPackageIcon}
                alt="Premium Tier Icon"
                style={{ width: "20px", height: "20px" }}
              />
              <BestDealContainer>
                <TextView
                  style={{
                    fontWeight: 700,
                    fontSize: "12px",
                    lineHeight: "16px",
                    whiteSpace: "nowrap",
                  }}
                >
                  Most Popular
                </TextView>
              </BestDealContainer>
            </>
          )}
          {sub_tier === 3 && (
            <img
              src={ProSuiteIcon}
              alt="Pro Suite Icon"
              style={{ width: "20px", height: "20px" }}
            />
          )}
        </TitleRow>
        <Description isMonthly={!isAnnually}>
          <TextView style={{ color: activeItem ? colors.white : "#565556" }}>
            {stripSuspendedReferralCopy(
              short_description,
              REFERRAL_PROGRAM_SUSPENDED
            )}
          </TextView>
        </Description>
        {/* <EarningsProgramWrapper>
          {(sub_tier === 2 || sub_tier === 3) && (
            <EarningsProgramContainer>
              <img
                src={sub_tier === 2 ? CoinIcon : CoinsIcon}
                alt="Earnings Icon"
                style={{
                  width: sub_tier === 3 ? "36px" : "24px",
                  height: sub_tier === 3 ? "24px" : "24px",
                }}
              />
              <TextView
                style={{
                  fontFamily: "DM Sans",
                  fontWeight: 700,
                  fontStyle: "Bold",
                  fontSize: "12px",
                  lineHeight: "20px",
                  letterSpacing: "0%",
                  verticalAlign: "middle",
                  color: "#123051",
                }}
              >
                Earnings Program
              </TextView>
              <StyledTooltip
                title={
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "10px", // Reduced gap
                      padding: "15px", // Reduced padding
                      minWidth: "400px",
                      // maxWidth: "400px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "5px", // Reduced gap
                      }}
                    >
                      <img
                        src={Coins3dIcon}
                        alt="Earnings Program Icon"
                        style={{ width: "28px", height: "28px" }} // Reduced icon size
                      />
                      <TextView
                        style={{
                          fontFamily: "DM Sans",
                          fontWeight: 700,
                          fontStyle: "Bold",
                          fontSize: "16px", // Reduced font size
                          lineHeight: "1.2",
                          letterSpacing: "0%",
                          color: "#363636",
                          height: "auto",
                          textAlign: "center",
                        }}
                      >
                        Earnings Program
                      </TextView>
                    </div>
                    <TextView style={{ fontWeight: 700, fontSize: "14px" }}>
                      🔗 Effortless Referrals
                    </TextView>
                    <TextView style={{ fontSize: "12px", color: "#6E6E6E" }}>
                      Anyone who subscribes while viewing your Wheels or Gallery
                      will be automatically linked to your Referral Code.
                    </TextView>
                    <TextView style={{ fontWeight: 700, fontSize: "14px" }}>
                      💸 Lifetime Earnings
                    </TextView>
                    <TextView style={{ fontSize: "12px", color: "#6E6E6E" }}>
                      As long as you maintain a Premium (or higher) subscription
                      & your referral remains a paying subscriber, you'll earn
                      every month.
                    </TextView>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "5px", // Reduced gap
                      }}
                    >
                      <img
                        src={PremiumPackageIcon}
                        alt="Premium Plan Icon"
                        style={{ width: "20px", height: "20px" }} // Reduced icon size
                      />
                      <TextView style={{ fontSize: "14px", color: "#363636" }}>
                        Premium Plan
                        <span style={{ fontWeight: 700 }}>→ 5% commission</span>
                      </TextView>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "5px", // Reduced gap
                      }}
                    >
                      <img
                        src={ProSuiteIcon}
                        alt="ProSuite Plan Icon"
                        style={{ width: "20px", height: "20px" }} // Reduced icon size
                      />
                      <TextView
                        style={{
                          fontSize: "14px", // Reduced font size
                          color: "#363636",
                        }}
                      >
                        ProSuite Plan
                        <span style={{ fontWeight: 700 }}>
                          → 10% commission
                        </span>
                      </TextView>
                    </div>
                    <div
                      style={{
                        border: "1px solid #F79A00",
                        borderRadius: "8px",
                        padding: "8px", // Reduced padding
                        display: "flex",
                        flexDirection: "column",
                        gap: "5px", // Reduced gap
                        backgroundColor: "#FCEFD8",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "5px", // Reduced gap
                        }}
                      >
                        <TextView
                          style={{
                            fontWeight: 700,
                            fontSize: "14px", // Reduced font size
                            color: "#363636",
                          }}
                        >
                          🚨 Coming Soon:
                        </TextView>
                      </div>
                      <TextView style={{ fontSize: "12px", color: "#6E6E6E" }}>
                        <b>Paid Locks</b> – Enables you to charge for access to
                        your Wheels!
                      </TextView>
                    </div>
                  </div>
                }
                arrow
                placement="top"
                color={"white"}
                textColor={"#123051"}
                sx={{
                  "& .MuiTooltip-tooltip": {
                    maxWidth: "600px !important",
                    width: "auto !important",
                    minWidth: "auto !important",
                    maxHeight: "auto", // Allow height to adjust to content
                    overflowY: "hidden", // Prevent vertical scrolling
                    overflowX: "hidden",
                    boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.3)",
                    borderRadius: "8px",
                    padding: "10px",
                  },
                }}
              >
                <InfoOutlinedIcon
                  style={{
                    fontSize: "16px",
                    color: "#123051",
                    cursor: "pointer",
                  }}
                />
              </StyledTooltip>
            </EarningsProgramContainer>
          )}
        </EarningsProgramWrapper> */}
        {is_enterprise ? (
          // No public price for the sales-led Enterprise tier (unit E4). "Custom"
          // fills the price slot so the card keeps visual parity with its siblings
          // without showing a dollar figure.
          <PriceContainer>
            <TextView variant="title" style={{ fontWeight: 700 }}>
              Custom
            </TextView>
            <TextView variant="text" style={{ color: "#565556" }}>
              Tailored to your organization
            </TextView>
          </PriceContainer>
        ) : (
          <PriceContainer>
            {annualCard ? (
              <AnnualYearlyPrice
                card={annualCard}
                activeItem={activeItem}
                showFeatures={showFeatures}
              />
            ) : (
            <>
            <StrikethroughRow data-testid="annual-strikethrough-row">
              {isDiscounted && (
                <TextView
                  variant="subtitle"
                  style={{
                    textDecoration: "line-through",
                    color: activeItem ? colors.white : colors.lightGrey,
                  }}
                >
                  $
                  {displayedOriginalCost}
                </TextView>
              )}
            </StrikethroughRow>

            {isUnpriced ? (
              // PLQA-C-002: this tier has no Stripe product linked yet, so
              // the $0 the server sends is "not priced yet", not "Free" --
              // labeling it "Free" (below) claimed a permanent zero cost
              // that directly contradicted this same card's per-seat
              // billing copy. Same span shape as the "Free" branch so the
              // PriceContainer height/CTA baseline stay identical.
              <TextView>
                <span
                  style={{
                    fontWeight: 700,
                    fontSize: "28px",
                    lineHeight: "21px",
                    ...(activeItem && { color: colors.white }),
                  }}
                >
                  Coming Soon
                </span>
              </TextView>
            ) : isFreeTier && !priceRepeatsName ? (
              // PLQA-C-006: render "Free" through the identical
              // fontSize/lineHeight span the paid price line uses below,
              // not TextView's `variant="title"` -- that variant's
              // browser-default line-height didn't match the paid line's
              // explicit 21px, so a zero-cost card's PriceContainer was a
              // few px shorter/taller than a paid card's and its Subscribe
              // CTA landed on a different baseline.
              <TextView>
                <span
                  style={{
                    fontWeight: 700,
                    fontSize: "28px",
                    lineHeight: "21px",
                    ...(activeItem && { color: colors.white }),
                  }}
                >
                  Free
                </span>
              </TextView>
            ) : (
              <TextView>
                <span
                  style={{
                    fontWeight: 700,
                    fontSize: "28px",
                    lineHeight: "21px",
                    ...(activeItem && { color: colors.white }),
                  }}
                >
                  $
                  {priceRepeatsName
                    ? 0
                    : isDiscounted
                      ? per_month_cost
                      : displayedOriginalCost}
                </span>
                <span
                  style={{
                    color: activeItem ? colors.white : "#565556",
                    marginLeft: "2px",
                    // relost-freeze-reverify-mktg-768-price-wordwrap: this
                    // span otherwise inherits `overflow-wrap: break-word`
                    // from TextView's baseStyles (components/TextView/
                    // index.jsx) with no width protection of its own --
                    // live-measured at 768px, ProSuite's price broke
                    // mid-word ("$20.83/mont" / "h"). Pin it so the whole
                    // "/month" word moves to a new line rather than
                    // splitting inside it.
                    overflowWrap: "normal",
                    whiteSpace: "nowrap",
                  }}
                >
                  /month
                </span>
              </TextView>
            )}

            {isAnnually && (
              // PLQA-C-006: mount this row unconditionally (same
              // always-mounted-but-reserved technique StrikethroughRow
              // already uses for the discount row) rather than skipping it
              // via `per_month_cost !== 0` -- that strict check disagreed
              // with `isFreeTier` above whenever `per_month_cost` arrived as
              // a string ("0.00" !== 0 is true with no coercion), so a
              // zero-cost tier could get a bogus "$0.00 Billed annually"
              // pill AND a taller PriceContainer than its zero-cost sibling
              // that used the numeric 0 shape. `visibility:hidden` (not
              // `display:none`) keeps the row's height reserved so every
              // annual-view card, free or paid, has the same PriceContainer
              // height and CTA baseline.
              <AnnualPrice style={isFreeTier ? { visibility: "hidden" } : undefined}>
                <AnnualPriceValue>
                  {`$${formatAnnualTotal(isDiscounted ? per_month_cost : displayedOriginalCost)}`}
                </AnnualPriceValue>
                <TextView variant="text" style={{ color: "#565556" }}>
                  Billed annually
                </TextView>
              </AnnualPrice>
            )}
            </>
            )}
          </PriceContainer>
        )}
      </Header>

      <ButtonContainer>
        {is_enterprise ? (
          // Sales-led CTA: a plain mailto anchor (no checkout / no Stripe). A real
          // link so it is natively keyboard-reachable and works without JS.
          <ContactSalesLink
            href={`mailto:${contact_email}`}
            aria-label={`${cta_label} — contact iYuga sales about Enterprise`}
          >
            {cta_label}
          </ContactSalesLink>
        ) : appStoreManaged && onMovePlanPress && action !== "ACTIVE" ? (
          // ISP-9999 (Option B4): a store subscriber CAN start a web plan --
          // it just can't be done through the normal purchase flow (that
          // still 400s cross-platform, see payments/serializers.py). Opens
          // MovePlanModal instead of the App Store deep link.
          <Button
            data-testid={`move-plan-${item?.id ?? "card"}`}
            onClick={() => onMovePlanPress(item)}
            variant={"outlined"}
            style={{
              width: "90%",
              margin: "auto",
              backgroundColor: colors.white,
              color: colors.PrimaryPurple700,
              cursor: "pointer",
            }}
          >
            Start on Web
          </Button>
        ) : appStoreManaged ? (
          // SUB-A4: this sub can only be changed where it was bought. The
          // authoritative multi-store choice lives in the status panel above
          // (Settings/Subscription/index.jsx); a single card button can only
          // open one destination, so it always opens the first resolved link
          // (App Store when the store is unknown -- see
          // storeManagementLinks.js for why the store is unknown today).
          <Button
            data-testid={`app-store-manage-${item?.id ?? "card"}`}
            onClick={() =>
              window.open(
                resolveAppStoreManagement(item?.payment_source).links[0].href,
                "_blank",
                "noopener,noreferrer"
              )
            }
            variant={"outlined"}
            style={{
              width: "90%",
              margin: "auto",
              backgroundColor: colors.white,
              color: colors.PrimaryPurple700,
              cursor: "pointer",
            }}
          >
            Manage in App Store
          </Button>
        ) : (
          <Button
            // billing-monetization S2.3 (gauntlet-billing 2026-08-06, critic-found):
            // `!activeItem && onActionPress` passes the literal boolean `false` as
            // onClick when activeItem is true — NOT `undefined` — so the shared
            // Button's `onClick = () => {}` default parameter (only applies to
            // undefined) never kicks in, Button.handleClick calls `onClick(e)`,
            // and `false(e)` throws `TypeError: onClick is not a function`. The
            // held-tier "Current Plan" card was never actually inert: it rendered
            // the right label but crashed on click. `disabled` was ALSO never
            // passed, so the CTA was never disabled at the DOM level either — the
            // whole inert-state was resting on this one broken falsy expression.
            // Fix: pass `undefined` (never a non-function truthy/falsy value) and
            // the real `disabled` prop, so the native <button disabled> attribute
            // stops the click before it ever reaches the handler.
            // PLQA-C-002: Business has no `stripe_product_id` yet -- the
            // checkout call 400s at serializer validation before Stripe is
            // ever reached, so this CTA must fail closed (disabled, no
            // click) the same way the held-tier "Current Plan" card does,
            // rather than let a user hit a dead-end error.
            onClick={activeItem || isUnpriced ? undefined : onActionPress}
            disabled={activeItem || isUnpriced}
            variant={"outlined"}
            style={{
              width: "90%",
              margin: "auto",
              backgroundColor:
                activeItem || isUnpriced
                  ? colors.PrimaryPurple100
                  : colors.white,
              // DL-030: the outlined tier CTA is purple TEXT on a light fill;
              // accent #9B5AF8 was only ~4.03:1 (FAILS AA). Use PrimaryPurple700
              // #8126DB (~6.5:1) — the exact colour E4's Enterprise "Let's talk"
              // button already uses, so all five tier CTAs read identically.
              // Inline style beats the shared Button's `.disabled` CSS class
              // (cascade order), so passing `disabled` above changes ONLY click
              // behavior — this held-tier styling is unchanged.
              color: colors.PrimaryPurple700,
              cursor: activeItem || isUnpriced ? "default" : "pointer",
              // T22.755: the `disabled` prop also adds the shared Button's
              // `disabled` class, and the GLOBAL `.disabled { opacity: 0.5 }`
              // rule (src/index.scss) then faded this whole CTA over the purple
              // held-tier card -- live-measured "Current Plan" at 2.39:1 on
              // web-stg (needs 4.5). The inert state is already conveyed by
              // the label, the PrimaryPurple100 fill and cursor:default, so keep
              // it fully opaque (PrimaryPurple700 on PrimaryPurple100 = 5.49:1).
              opacity: 1,
            }}
          >
            {isUnpriced ? "Coming Soon" : getButtonText()}
          </Button>
        )}
      </ButtonContainer>

      {showFeatures && (
        <FeatureItemContainer ref={featureListRef}>
          <FeaturesList>
            {featureItems.map((feature, index) => (
              <FeatureItem key={index}>
                <img
                  src={activeItem ? CheckCircleWhite : CheckCircle}
                  alt="check"
                />
                <TextView
                  variant="text"
                  style={{
                    color: activeItem ? colors.white : "#565556",
                    flex: 1,
                    minWidth: 0,
                  }}
                  dangerouslySetInnerHTML={{
                    __html: displayFeatureDescription(
                      feature.feature_description,
                      paidLocksLive
                    ),
                  }}
                />
                {typeof feature.tooltip === "string" && (
                  <StyledTooltip
                    title={
                      <div
                        style={{ textAlign: "left" }}
                        dangerouslySetInnerHTML={{ __html: feature.tooltip }}
                      ></div>
                    }
                    arrow
                    placement="top"
                    color={"#FCEFD8"}
                    textColor={"#123051"}
                    width={"206px"}
                    // nem-sub-tooltip-overlap-cta: without a boundary, MUI's
                    // default collision box is the viewport/scroll ancestors
                    // — plenty of room on the tight pricing grid to spill
                    // sideways into the NEXT tier card's "Subscribe" button.
                    // Bounding to `featureListEl` (this feature list's own
                    // node, below the Subscribe ButtonContainer) rather than
                    // the whole card keeps the same horizontal containment
                    // (still can't spill into a neighboring card) while ALSO
                    // stopping the tooltip from escaping upward on top of
                    // THIS card's own CTA button: `cardEl` alone didn't catch
                    // that, since the button lives inside the card boundary
                    // too, so covering it never registered as overflow —
                    // live-measured painting a 72x32px tooltip directly over
                    // "Subscribe to Premium"/Essential/ProSuite for the first
                    // 1-2 feature rows before this fix.
                    slotProps={{
                      popper: {
                        modifiers: [
                          {
                            name: "preventOverflow",
                            options: {
                              boundary: featureListEl,
                              altAxis: true,
                              padding: 8,
                            },
                          },
                          {
                            name: "flip",
                            options: {
                              boundary: featureListEl,
                              fallbackPlacements: [
                                "bottom",
                                "top-start",
                                "top-end",
                              ],
                            },
                          },
                        ],
                      },
                    }}
                  >
                    <InfoOutlinedIcon
                      style={featureInfoIconStyle(
                        activeItem ? colors.white : "#123051"
                      )}
                    />
                  </StyledTooltip>
                )}
              </FeatureItem>
            ))}
          </FeaturesList>
          {!is_enterprise && (
            // PLQA-C-004: this link opens the same comparison table via the
            // same onViewFeatures handler regardless of activeItem -- there
            // is no functional difference to signal, so every tier (active
            // plan included) reads "View all Features" instead of the
            // current plan alone reading "Compare".
            <FeaturesLink
              variant="subtitle"
              onClick={onViewFeatures}
              isActive={activeItem}
            >
              View all Features
            </FeaturesLink>
          )}
        </FeatureItemContainer>
      )}
    </Container>
  );
}
